if (!customElements.get("td-mobile-menu")) {
  class TDMobileMenu extends HTMLElement {
    constructor() {
      super();
      
      // Configuration - matches Timber defaults
      this.config = {
        close: ".js-drawer-close",
        open: ".js-drawer-open-button-left",
        openButtonLeftClass: "js-drawer-open-button-left",
        drawerLeftClass: "drawer--left",
        drawerRightClass: "drawer--right",
        openClass: "js-drawer-open",
        dirOpenClass: "js-drawer-open-left"
      };
      
      // State
      this.drawerIsOpen = false;
      this.activeSource = null;
      
      // DOM elements
      this.drawer = null;
      this.openBtn = null;
      this.closeBtn = null;
      this.parent = null;
      this.page = null;
      this.moved = null;
      
      // Event handlers (bound for easy removal)
      this.boundOpenHandler = this.open.bind(this);
      this.boundCloseHandler = this.close.bind(this);
      this.boundKeyupHandler = this.handleKeyup.bind(this);
      this.boundTouchMoveHandler = this.handleTouchMove.bind(this);
      this.boundDrawerClickHandler = this.handleDrawerClick.bind(this);
      this.boundOutsideClickHandler = this.handleOutsideClick.bind(this);
      this.boundActiveSourceClickHandler = this.handleActiveSourceClick.bind(this);
    }
    
    connectedCallback() {
      this.init();
    }
    
    disconnectedCallback() {
      this.cleanup();
    }
    
    init() {
      this.cacheElements();
      if (!this.drawer) {
        if (DEBUG) console.log("TD Mobile Menu: Drawer not found");
        return;
      }
      
      this.setupEventListeners();
      this.setupAriaAttributes();
      
      if (DEBUG) console.log("TD Mobile Menu: Initialized");
    }
    
    cacheElements() {
      // Cache drawer element
      this.drawer = document.getElementById("NavDrawer");
      
      // Cache open button
      this.openBtn = document.querySelector(this.config.open);
      
      // Cache close button
      this.closeBtn = this.drawer?.querySelector(this.config.close);
      
      // Cache parent elements
      this.parent = document.querySelector("body, html");
      this.page = document.getElementById("PageContainer");
      this.moved = document.querySelector(".page-container");
    }
    
    setupEventListeners() {
      if (this.openBtn) {
        this.openBtn.addEventListener("click", this.boundOpenHandler);
      }
      
      if (this.closeBtn) {
        this.closeBtn.addEventListener("click", this.boundCloseHandler);
      }
    }
    
    setupAriaAttributes() {
      if (this.openBtn) {
        this.openBtn.setAttribute("aria-expanded", "false");
      }
    }
    
    open(evt) {
      // Keep track if drawer was opened from a click, or called by another function
      const externalCall = !evt;
      
      // Don't open an opened drawer
      if (this.drawerIsOpen) {
        if (evt) {
          evt.preventDefault();
        }
        return;
      }
      
      // Prevent following href if link is clicked
      if (evt) {
        evt.preventDefault();
      }
      
      // Without this, the drawer opens, the click event bubbles up to page
      // which closes the drawer.
      if (evt && evt.stopPropagation) {
        evt.stopPropagation();
        // save the source of the click, we'll focus to this on close
        this.activeSource = evt.currentTarget;
      }
      
      if (this.drawerIsOpen && !externalCall) {
        return this.close();
      }
      
      if (this.moved) {
        this.moved.classList.add("is-transitioning");
      }
      
      // Prepare transition
      this.prepareTransition(this.drawer);
      
      // Add open classes
      this.parent.classList.add(this.config.openClass, this.config.dirOpenClass);
      this.drawer.classList.add(this.config.openClass);
      
      this.drawerIsOpen = true;
      
      // Update aria-expanded attribute
      if (this.openBtn) {
        this.openBtn.setAttribute("aria-expanded", "true");
      }
      
      // Set focus on drawer
      this.trapFocus({
        container: this.drawer,
        namespace: "drawer_focus"
      });
      
      // Run TD custom onDrawerOpen callback
      this.onDrawerOpen();
      
      // Bind events for open state
      this.bindEvents();
      
      if (DEBUG) console.log("TD Mobile Menu: Opened");
    }
    
    close(evt) {
      if (!this.drawerIsOpen) {
        return;
      }
      
      // Prevent following href if link is clicked
      if (evt) {
        evt.preventDefault();
      }
      
      // Remove open classes
      this.parent.classList.remove(this.config.openClass, this.config.dirOpenClass);
      this.drawer.classList.remove(this.config.openClass);

      this.drawerIsOpen = false;
      
      // Update aria-expanded attribute
      if (this.openBtn) {
        this.openBtn.setAttribute("aria-expanded", "false");
      }
      
      this.onDrawerClose();
      this.unbindEvents();
      
      // Return focus to source element
      if (this.activeSource) {
        this.activeSource.focus();
        this.activeSource = null;
      }
      
      if (DEBUG) console.log("TD Mobile Menu: Closed");
    }
    
    toggle(evt) {
      if (this.drawerIsOpen) {
        this.close(evt);
      } else {
        this.open(evt);
      }
    }
    
    bindEvents() {
      // Lock scrolling on mobile
      if (this.page) {
        this.page.addEventListener("touchmove", this.boundTouchMoveHandler, { passive: false });
      }
      
      // Prevent drawer click from closing drawer
      if (this.drawer) {
        this.drawer.addEventListener("click", this.boundDrawerClickHandler);
      }
      
      this.parent.addEventListener("keyup", this.boundKeyupHandler);
      
      // Handle active source click (hamburger becomes close button)
      if (this.activeSource) {
        this.activeSource.addEventListener("click", this.boundActiveSourceClickHandler);
      }
    }
    
    unbindEvents() {
      // Remove touch move handler
      if (this.page) {
        this.page.removeEventListener("touchmove", this.boundTouchMoveHandler);
      }
      
      // Remove drawer click handler
      if (this.drawer) {
        this.drawer.removeEventListener("click", this.boundDrawerClickHandler);
      }
      
      // Remove keyup handler
      this.parent.removeEventListener("keyup", this.boundKeyupHandler);
      
      // Remove active source click handler
      if (this.activeSource) {
        this.activeSource.removeEventListener("click", this.boundActiveSourceClickHandler);
      }
    }
    
    handleTouchMove(evt) {
      // Lock scrolling on mobile when drawer is open
      evt.preventDefault();
      return false;
    }
    
    handleDrawerClick(evt) {
      if (this.drawer.classList.contains("drawer--left")) {
        evt.stopPropagation();
      }
    }
    
    handleOutsideClick(evt) {
      this.close(evt);
    }
    
    handleKeyup(evt) {
      // Handle escape key
      if (evt.keyCode === 27) {
        this.close(evt);
      }
    }
    
    handleActiveSourceClick(evt) {
      if (!this.activeSource.classList.contains(this.config.openButtonLeftClass)) {
        return;
      }
      this.close(evt);
    }
    
    onDrawerOpen() {
      document.documentElement.style.cssText = "overflow: hidden !important;";
      document.querySelector("body").style.overflow = "hidden";

      // Align drawer top to the bottom of the header nav
      const header = document.querySelector(".oddit-site-header");
      if (header && this.drawer) {
        const headerBottom = header.getBoundingClientRect().bottom - 1;
        this.drawer.style.top = headerBottom + "px";
        this.drawer.style.height = "";
      }

      // Reinitialize swiper in drawer menu
      const drawerSwipers = this.drawer.querySelectorAll('swiper-slider[data-custom-event="reinitialize"]');
      drawerSwipers.forEach(swiper => {
        swiper.dispatchEvent(new CustomEvent('reinitialize-swiper'));
      });
    }

    onDrawerClose() {
      document.documentElement.style.overflow = null;
      
      setTimeout(() => {
        document.getElementById("NavDrawer").style.height = 0;
      }, 350);
      
      document.querySelector("body").style.overflow = null;
    }
    
    // Focus trap functionality
    trapFocus(options) {
      const container = options.container;
      const elements = this.getFocusableElements(container);
      const firstElement = elements[0];
      const lastElement = elements[elements.length - 1];
      
      if (!firstElement) return;
      
      container.setAttribute("tabindex", "-1");
      container.focus();
      
      const trapFocusHandler = (evt) => {
        if (evt.keyCode !== 9) return; // Tab key
        
        if (evt.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus();
            evt.preventDefault();
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus();
            evt.preventDefault();
          }
        }
      };
      
      document.addEventListener("keydown", trapFocusHandler);
      
      // Store handler for cleanup
      this.trapFocusHandler = trapFocusHandler;
    }
    
    getFocusableElements(container) {
      const focusableElements = [
        "a[href]",
        "button:not([disabled])",
        "textarea:not([disabled])",
        "input:not([disabled])",
        "select:not([disabled])",
        "[tabindex]:not([tabindex='-1'])"
      ];
      
      return container.querySelectorAll(focusableElements.join(", "));
    }
    
    // Prepare transition
    prepareTransition(element) {
      element.style.transition = "transform 0.3s ease";
    }
    
    cleanup() {
      // Remove all event listeners
      this.unbindEvents();
      
      // Remove trap focus handler
      if (this.trapFocusHandler) {
        document.removeEventListener("keydown", this.trapFocusHandler);
      }
      
      // Reset state
      this.drawerIsOpen = false;
      this.activeSource = null;
      
      if (DEBUG) console.log("TD Mobile Menu: Cleaned up");
    }
    
    // Public API methods for external access
    openPublic() {
      this.open();
    }
    
    closePublic() {
      this.close();
    }
    
    togglePublic() {
      this.toggle();
    }
    
    isOpen() {
      return this.drawerIsOpen;
    }
  }

  const DEBUG = false;
  customElements.define("td-mobile-menu", TDMobileMenu);
}
